import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { usePeriod } from './PeriodContext';
import { getPeriodData } from './dataService';

function Kegiatan() {
  const { selectedPeriod, periodImages } = usePeriod();
  const [kegiatans, setKegiatans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);

  // Parallax Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Background image: use period-specific first, then fallback to default
  const backgroundImage = periodImages?.backgroundImage 
    ? periodImages.backgroundImage 
    : (selectedPeriod === '2025-2026' ? '/bg.jpg' : '/fkpoo.JPG');

  // Fetch kegiatan from Firestore based on selected period
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const data = await getPeriodData(selectedPeriod);
        if (data && data.kegiatans && Array.isArray(data.kegiatans)) {
          setKegiatans(data.kegiatans);
        } else {
          // Default empty array if no data
          setKegiatans([]);
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
        setKegiatans([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivities();
  }, [selectedPeriod]);

  // Default activities for reference (used when no data in Firestore)
  const defaultActivities = {
    '2025-2026': [
      {
        title: "Rapat Rutin",
        date: "Agenda Berkala / Bulanan",
        location: "SMK Negeri 1 Pengasih",
        category: "Koordinasi Internal",
        image: "/rapat.jpg",
        type: "history",
        description: "Rapat rutin FKPO Kulon Progo merupakan agenda berkala yang menjadi wadah konsolidasi, koordinasi, dan evaluasi bagi seluruh pengurus."
      },
      {
        title: "Rewang Bersinar",
        date: "Ramadhan 2025",
        location: "Panti Asuhan",
        category: "Sosial & Kolaborasi",
        image: "/rewang.JPEG",
        type: "history",
        description: "Program sosial kolaborasi FKPO, Foksit DIY, dan Forku. Mengusung semangat 'rewang' (gotong royong), kegiatan ini mengajak pelajar berbagi kebutuhan pokok."
      }
    ]
  };

  const activities = kegiatans.length > 0 ? kegiatans : (defaultActivities[selectedPeriod] || []);

  // Pisahkan kegiatan berdasarkan tipe
  const nextEvents = activities.filter(keg => keg.type === 'next' || !keg.type); // Default ke next jika tidak ada type
  const historyEvents = activities.filter(keg => keg.type === 'history');

  // Fallback image if activity doesn't have one
  const getImageSrc = (img) => {
    if (!img || img.trim() === '') return '/bg.jpg';
    // Jika sudah merupakan URL lengkap (http/https), gunakan langsung
    if (img.startsWith('http://') || img.startsWith('https://')) {
      return img;
    }
    // Jika format lokal (/xxx.jpg), gunakan sebagai public path
    return img;
  };

  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 overflow-hidden">
      {/* Background Layer (Fixed & Calm) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25 filter blur-[1px] pointer-events-none"
        style={{ 
          backgroundImage: `url(${backgroundImage})`
        }}
      />

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />

      {/* Dynamic Animated Blobs */}
      <div 
        className="absolute top-16 left-[-100px] w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[130px] animate-blob-1 pointer-events-none"
        style={{ transform: `translateY(${scrollY * -0.15}px)` }}
      />
      <div 
        className="absolute top-1/2 right-[-100px] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[140px] animate-blob-2 pointer-events-none"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      />

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center">
        <main className="flex-grow pt-28 pb-16 text-slate-100 min-h-screen">
          <div className="container mx-auto px-4 sm:px-6">
            
            {/* --- HEADER SECTION with Parallax Lift --- */}
            <div 
              className="text-center mb-16 space-y-4 transition-transform duration-200 ease-out"
              style={{ transform: `translateY(${scrollY * -0.05}px)` }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 backdrop-blur-md text-indigo-300 text-xs font-bold uppercase tracking-wider shadow-lg shadow-indigo-950/40">
                <Calendar className="w-3.5 h-3.5" />
                <span>Agenda & Program Kerja</span>
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight">
                Jejak Langkah <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 to-pink-400 drop-shadow-[0_0_30px_rgba(168,85,247,0.3)]">Perubahan</span>
              </h1>
              <p className="text-base sm:text-xl text-slate-300/90 max-w-3xl mx-auto leading-relaxed">
                Simak berbagai kegiatan unggulan kami yang dirancang untuk membangun karakter, solidaritas, dan dampak sosial nyata.
              </p>
            </div>

            {/* --- FILTER & SEARCH BUTTONS --- */}
            {!loading && activities.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
                <span className="text-xs uppercase font-bold text-slate-400 tracking-wider mr-2">
                  Kategori:
                </span>
                <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-indigo-600 text-white shadow-md shadow-indigo-600/30">
                  Semua ({activities.length})
                </span>
                <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-slate-900 border border-slate-800 text-slate-300">
                  Mendatang ({nextEvents.length})
                </span>
                <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-slate-900 border border-slate-800 text-slate-300">
                  Terlaksana ({historyEvents.length})
                </span>
              </div>
            )}

            {/* --- CONDITIONAL CONTENT --- */}
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-400 mt-4 text-sm">Memuat agenda kegiatan...</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center max-w-2xl mx-auto">
                <div className="bg-slate-900/80 border border-slate-800/80 rounded-3xl p-12 backdrop-blur-md shadow-xl">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Belum Ada Agenda</h2>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Agenda kegiatan untuk periode <span className="text-indigo-400 font-semibold">{selectedPeriod}</span> belum dirilis atau sedang dalam tahap penyusunan.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-20">
                {/* --- NEXT EVENTS SECTION --- */}
                {nextEvents.length > 0 && (
                  <div>
                    <div className="text-center mb-10">
                      <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></span>
                        Agenda Mendatang
                      </div>
                      <h2 className="text-3xl font-extrabold text-white">
                        Next <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Events</span>
                      </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                      {nextEvents.map((item, index) => (
                        <div
                          key={`next-${index}`}
                          className="group bg-slate-900/60 hover:bg-slate-900/90 rounded-2xl overflow-hidden border border-slate-800 hover:border-indigo-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 flex flex-col backdrop-blur-md hover:-translate-y-1"
                        >

                          {/* --- IMAGE AREA --- */}
                          <div className="relative h-60 w-full overflow-hidden bg-slate-950">
                            <img
                              src={getImageSrc(item.image)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = '/bg.jpg';
                              }}
                            />

                            {/* Overlay Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-80"></div>

                            {/* Badges */}
                            <div className="absolute top-4 left-4 z-10 flex gap-2">
                              <span className="px-3 py-1 bg-indigo-600/90 backdrop-blur-md text-[11px] font-bold text-white rounded-lg border border-indigo-400/40 shadow-lg">
                                UPCOMING
                              </span>
                              <span className="px-3 py-1 bg-slate-950/80 backdrop-blur-md text-[11px] font-medium text-slate-300 rounded-lg border border-slate-800 shadow-lg">
                                {item.category || 'Program'}
                              </span>
                            </div>
                          </div>

                          {/* --- CONTENT AREA --- */}
                          <div className="p-7 flex flex-col flex-grow relative">
                            {/* Meta Info */}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mb-3.5">
                              <div className="flex items-center gap-1.5 bg-slate-800/80 px-2.5 py-1 rounded-md text-indigo-300 font-medium">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{item.date || 'Waktu Belum Ditentukan'}</span>
                              </div>
                              <div className="flex items-center gap-1.5 bg-slate-800/50 px-2.5 py-1 rounded-md text-slate-300">
                                <MapPin className="w-3.5 h-3.5 text-indigo-400" />
                                <span>{item.location || 'Kab. Kulon Progo'}</span>
                              </div>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-3 group-hover:text-indigo-400 transition-colors">
                              {item.title || 'Judul Kegiatan'}
                            </h3>

                            <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-grow">
                              {item.description || 'Deskripsi kegiatan belum tersedia.'}
                            </p>

                            <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs text-indigo-400 font-semibold">
                              <span className="flex items-center gap-1.5 group-hover:text-indigo-300 transition-colors">
                                Informasi Selengkapnya
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                              </span>
                            </div>
                          </div>

                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* --- HISTORY SECTION --- */}
                {historyEvents.length > 0 && (
                  <div>
                    <div className="text-center mb-10">
                      <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs font-bold uppercase tracking-wider mb-2">
                        Dokumentasi Kegiatan
                      </div>
                      <h2 className="text-3xl font-extrabold text-white">
                        History <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Events</span>
                      </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                      {historyEvents.map((item, index) => (
                        <div
                          key={`history-${index}`}
                          className="group bg-slate-900/60 hover:bg-slate-900/90 rounded-2xl overflow-hidden border border-slate-800 hover:border-amber-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/10 flex flex-col backdrop-blur-md hover:-translate-y-1"
                        >

                          {/* --- IMAGE AREA --- */}
                          <div className="relative h-60 w-full overflow-hidden bg-slate-950">
                            <img
                              src={getImageSrc(item.image)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = '/bg.jpg';
                              }}
                            />

                            {/* Overlay Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-80"></div>

                            {/* Badges */}
                            <div className="absolute top-4 left-4 z-10 flex gap-2">
                              <span className="px-3 py-1 bg-amber-600/90 backdrop-blur-md text-[11px] font-bold text-white rounded-lg border border-amber-400/40 shadow-lg">
                                TERLAKSANA
                              </span>
                              <span className="px-3 py-1 bg-slate-950/80 backdrop-blur-md text-[11px] font-medium text-slate-300 rounded-lg border border-slate-800 shadow-lg">
                                {item.category || 'Dokumentasi'}
                              </span>
                            </div>
                          </div>

                          {/* --- CONTENT AREA --- */}
                          <div className="p-7 flex flex-col flex-grow relative">
                            {/* Meta Info */}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mb-3.5">
                              <div className="flex items-center gap-1.5 bg-slate-800/80 px-2.5 py-1 rounded-md text-amber-300 font-medium">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{item.date || 'Tanggal Pelaksanaan'}</span>
                              </div>
                              <div className="flex items-center gap-1.5 bg-slate-800/50 px-2.5 py-1 rounded-md text-slate-300">
                                <MapPin className="w-3.5 h-3.5 text-amber-400" />
                                <span>{item.location || 'Kulon Progo'}</span>
                              </div>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">
                              {item.title || 'Judul Kegiatan'}
                            </h3>

                            <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-grow">
                              {item.description || 'Deskripsi kegiatan belum tersedia.'}
                            </p>

                            <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs text-amber-400 font-semibold">
                              <span className="flex items-center gap-1.5 group-hover:text-amber-300 transition-colors">
                                Dokumentasi & Evaluasi
                                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                              </span>
                            </div>
                          </div>

                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* --- CTA SECTION --- */}
            <div className="mt-20 text-center relative max-w-4xl mx-auto overflow-hidden rounded-3xl p-10 bg-slate-900/80 border border-slate-800 backdrop-blur-xl shadow-2xl">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="relative z-10 space-y-4">
                <span className="text-xs uppercase font-bold tracking-widest text-indigo-400">Bergabung Bersama Kami</span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                  Ingin Terlibat dalam Gerakan Berikutnya?
                </h3>
                <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
                  Setiap langkah kecil membawa dampak nyata bagi pelajar se-Kulon Progo. Jadilah penggerak masa depan sekarang juga.
                </p>
                <div className="pt-2">
                  <a 
                    href="/daftar2027" 
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/50 hover:scale-[1.02] active:scale-95 text-sm sm:text-base"
                  >
                    <span>Daftar Pengurus Baru</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
            
          </div>
        </main>
      </div>
    </div>
  );
}

export default Kegiatan;

